export interface INavigationRoute {
  name: string
  displayName: string
  meta: { icon: string; allowedRoles?: string[] }
  children?: INavigationRoute[]
}

import { getUserRole } from '../../utils/userRoleUtils'

// Don't call getUserRole at module load time - it will be called when needed
const filterRoutesByRole = (routes: INavigationRoute[], role: string): INavigationRoute[] => {
  // Treat client_portal as having admin-level visibility for navigation
  const effectiveRole = role === 'client_portal' ? 'admin' : role

  return routes
    .filter((route) => {
      // Check if the route has allowed roles, and if the user's role is allowed
      if (route.meta.allowedRoles && route.meta.allowedRoles.includes(effectiveRole)) {
        return true // Accessible by the user's role
      }
      // Fallback condition for routes without role restrictions (e.g., dashboard)
      if (!route.meta.allowedRoles) {
        return true // Accessible by everyone
      }
      return false // Hide route if not allowed for the user's role
    })
    .map((route) => {
      // Recursively filter children routes if any
      if (route.children) {
        route.children = filterRoutesByRole(route.children, role)
      }
      return route
    })
}

const sidebarRoutes = {
  root: {
    name: '/',
    displayName: 'navigationRoutes.home',
  },
  routes: [
    {
      name: 'dashboard',
      displayName: 'Monthly Dashboard',
      meta: {
        icon: 'dashboard',
        allowedRoles: ['admin', 'manager', 'org_admin'],
      },
    },
    {
      name: 'performance',
      displayName: 'Driver Performance Dashboard',
      meta: {
        icon: 'analytics',
        allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
      },
    },
    {
      name: 'scorecard',
      displayName: 'Scorecard',
      meta: {
        icon: 'assessment',
        allowedRoles: ['admin', 'operator', 'manager', 'org_admin', 'analyst', 'finance'],
      },
    },
    {
      name: 'ev-dashboard',
      displayName: 'EV Dashboard',
      meta: {
        icon: 'electric_car',
        allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
      },
      children: [
        {
          name: 'charging-stations',
          displayName: 'Charging Stations',
          meta: {
            icon: 'ev_station',
            allowedRoles: [
              'admin',
              'operator',
              'manager',
              'org_admin',
              'support_agent',
              'guest',
              'team_manager',
              'finance',
              'finance-staff',
            ],
          },
        },
        {
          name: 'charging-sessions',
          displayName: 'Charging Sessions',
          meta: {
            icon: 'bolt',
            allowedRoles: [
              'admin',
              'operator',
              'manager',
              'org_admin',
              'support_agent',
              'guest',
              'team_manager',
              'finance',
              'finance-staff',
            ],
          },
        },
        {
          name: 'charging-station-settings',
          displayName: 'Charging Station Settings',
          meta: {
            icon: 'settings',
            allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
          },
        },
      ],
    },
    {
      name: 'valt-v2-drivers',
      displayName: 'Drivers',
      meta: {
        icon: 'moped',
        allowedRoles: [
          'admin',
          'operator',
          'manager',
          'org_admin',
          'owner',
          'recruiter',
          'support_agent',
          'guest',
          'team_manager',
          'analyst',
          'finance',
          'finance-staff',
          'developer',
          'driver',
          'client_portal',
          'analyst_viewer_org',
        ],
      },
      children: [
        {
          name: 'valt-v2-valt-drivers',
          displayName: 'Valt Drivers',
          meta: {
            icon: 'person',
            allowedRoles: [
              'operator',
              'admin',
              'manager',
              'org_admin',
              'owner',
              'recruiter',
              'support_agent',
              'guest',
              'team_manager',
              'analyst',
              'finance',
              'finance-staff',
              'developer',
              'driver',
              'client_portal',
              'analyst_viewer_org',
            ],
          },
        },
        {
          name: 'uber-deployed-drivers',
          displayName: 'Uber Deployed Drivers',
          meta: {
            icon: 'deployed_code',
            allowedRoles: [
              'operator',
              'admin',
              'manager',
              'org_admin',
              'owner',
              'recruiter',
              'support_agent',
              'guest',
              'team_manager',
              'finance',
              'finance-staff',
            ],
          },
        },
      ],
    },
    {
      name: 'valt-v2-vehicles',
      displayName: 'Vehicles',
      meta: {
        icon: 'directions_car',
        allowedRoles: [
          'admin',
          'operator',
          'manager',
          'org_admin',
          'owner',
          'recruiter',
          'support_agent',
          'guest',
          'team_manager',
          'analyst',
          'finance',
          'finance-staff',
          'developer',
          'driver',
          'client_portal',
          'analyst_viewer_org',
        ],
      },
      children: [
        {
          name: 'valt-v2-valt-vehicles',
          displayName: 'Valt Vehicles',
          meta: {
            icon: 'directions_car',
            allowedRoles: [
              'operator',
              'admin',
              'manager',
              'org_admin',
              'owner',
              'recruiter',
              'support_agent',
              'guest',
              'team_manager',
              'analyst',
              'finance',
              'finance-staff',
              'developer',
              'driver',
              'client_portal',
              'analyst_viewer_org',
            ],
          },
        },
        {
          name: 'go-metro-vehicles',
          displayName: 'Go Metro Vehicles',
          meta: {
            icon: 'commute',
            allowedRoles: [
              'operator',
              'admin',
              'manager',
              'org_admin',
              'owner',
              'recruiter',
              'support_agent',
              'guest',
              'team_manager',
              'analyst',
              'finance',
              'finance-staff',
              'developer',
              'driver',
              'client_portal',
              'analyst_viewer_org',
            ],
          },
        },
        // Aeversa integration section has been removed from the main admin sidebar
        {
          name: 'uteh-vehicles',
          displayName: 'UTEH Vehicles',
          meta: {
            icon: 'sync_alt',
            allowedRoles: [
              'operator',
              'admin',
              'manager',
              'org_admin',
              'owner',
              'recruiter',
              'support_agent',
              'guest',
              'team_manager',
              'analyst',
              'finance',
              'finance-staff',
              'developer',
              'driver',
              'client_portal',
              'analyst_viewer_org',
            ],
          },
        },
        {
          name: 'valt-v2-uber-vehicles',
          displayName: 'Uber Vehicles',
          meta: {
            icon: 'commute',
            allowedRoles: [
              'operator',
              'admin',
              'manager',
              'org_admin',
              'owner',
              'recruiter',
              'support_agent',
              'guest',
              'team_manager',
            ],
          },
        },
        {
          name: 'bikes',
          displayName: 'Bikes',
          meta: {
            icon: 'two_wheeler',
            allowedRoles: [
              'operator',
              'admin',
              'manager',
              'org_admin',
              'owner',
              'recruiter',
              'support_agent',
              'guest',
              'team_manager',
              'analyst',
              'finance',
              'finance-staff',
              'developer',
              'driver',
              'client_portal',
              'analyst_viewer_org',
            ],
          },
        },
      ],
    },
    {
      name: 'vehicle-mapping',
      displayName: 'Vehicle Mapping',
      meta: {
        icon: 'link',
        allowedRoles: ['admin', 'operator', 'manager', 'org_admin', 'owner', 'support_agent'],
      },
    },
    // {
    //   name: 'users',
    //   displayName: 'Users',
    //   meta: {
    //     icon: 'group',
    //     allowedRoles: ['admin', 'manager', 'org_admin'],
    //   },
    // },
    // {
    //   name: 'driver-activity',
    //   displayName: 'Driver Activity',
    //   meta: {
    //     icon: 'analytics',
    //     allowedRoles: [
    //       'admin',
    //       'operator',
    //       'manager',
    //       'org_admin',
    //       'owner',
    //       'analyst',
    //       'finance',
    //       'developer',
    //       'driver',
    //       'recruiter',
    //     ],
    //   },
    //   children: [
    //     {
    //       name: 'driver-activity',
    //       displayName: 'Driver Activity (Leaflet)',
    //       meta: {
    //         icon: 'map',
    //         allowedRoles: [
    //           'admin',
    //           'operator',
    //           'manager',
    //           'org_admin',
    //           'owner',
    //           'analyst',
    //           'finance',
    //           'developer',
    //           'driver',
    //           'recruiter',
    //         ],
    //       },
    //     },
    //     {
    //       name: 'driver-activity-google',
    //       displayName: 'Driver Activity (Google Maps)',
    //       meta: {
    //         icon: 'public',
    //         allowedRoles: [
    //           'admin',
    //           'operator',
    //           'manager',
    //           'org_admin',
    //           'owner',
    //           'analyst',
    //           'finance',
    //           'developer',
    //           'driver',
    //           'recruiter',
    //         ],
    //       },
    //     },
    //   ],
    // },
    // {
    //   name: 'messages',
    //   displayName: 'Messages',
    //   meta: {
    //     icon: 'message',
    //     allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
    //   },
    // },
    {
      name: 'driver-training',
      displayName: 'Driver Training',
      meta: {
        icon: 'school',
        allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
      },
    },
    {
      name: 'reports',
      displayName: 'Reports',
      meta: {
        icon: 'today',
        allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
      },
      children: [
        {
          name: 'reports',
          displayName: 'All Reports',
          meta: {
            icon: 'assessment',
            allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
          },
        },
        {
          name: 'odometer-reports',
          displayName: 'Odometer Reports',
          meta: {
            icon: 'speed',
            allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
          },
        },
        {
          name: 'charging-reports',
          displayName: 'Charging Reports',
          meta: {
            icon: 'battery_charging_full',
            allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
          },
        },
      ],
    },
    // {
    //   name: 'issues',
    //   displayName: 'Tickets',
    //   meta: {
    //     icon: 'dataset',
    //     allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
    //   },
    // },
    {
      name: 'alarms',
      displayName: 'Alarms',
      meta: {
        icon: 'notifications',
        allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
      },
    },
    {
      name: 'trips',
      displayName: 'Trips',
      meta: {
        icon: 'folder_shared',
        allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
      },
    },
    // {
    //   name: 'analytics',
    //   displayName: 'Analytics',
    //   meta: {
    //     icon: 'analytics',
    //     allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
    //   },
    // },
    {
      name: 'teams',
      displayName: 'Teams',
      meta: {
        icon: 'folder',
        allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
      },
    },
    {
      name: 'operators',
      displayName: 'Operators',
      meta: {
        icon: 'support_agent',
        allowedRoles: ['admin', 'manager', 'org_admin'],
      },
      children: [
        {
          name: 'operators',
          displayName: 'Operator Users',
          meta: {
            icon: 'support_agent',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
        {
          name: 'violations-escalation',
          displayName: 'Violations Escalation',
          meta: {
            icon: 'gpp_maybe',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
      ],
    },
    {
      name: 'tickets',
      displayName: 'Tickets',
      meta: {
        icon: 'assignment',
        allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
      },
    },
    {
      name: 'maintenance',
      displayName: 'Maintenance',
      meta: {
        icon: 'engineering',
        allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
      },
      children: [
        {
          name: 'maintenance',
          displayName: 'Maintenance List',
          meta: {
            icon: 'list',
            allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
          },
        },
        {
          name: 'maintenance-items',
          displayName: 'Maintenance Items',
          meta: {
            icon: 'inventory',
            allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
          },
        },
        {
          name: 'maintenance-workshops',
          displayName: 'Workshops',
          meta: {
            icon: 'business',
            allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
          },
        },
      ],
    },
    {
      name: 'admin-management',
      displayName: 'Admin Management',
      meta: {
        icon: 'admin_panel_settings',
        allowedRoles: ['admin', 'manager', 'org_admin'],
      },
      children: [
        {
          name: 'recruiter-dashboard',
          displayName: 'Recruiter Dashboard',
          meta: {
            icon: 'person_add',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
        {
          name: 'driver-training',
          displayName: 'Driver Training',
          meta: {
            icon: 'school',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
        {
          name: 'training-types',
          displayName: 'Training Types',
          meta: {
            icon: 'category',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
        // {
        //   name: 'role-management',
        //   displayName: 'Role Management',
        //   meta: {
        //     icon: 'security',
        //     allowedRoles: ['admin', 'org_admin'],
        //   },
        // },
        {
          name: 'ticket-management',
          displayName: 'Ticket Management',
          meta: {
            icon: 'support',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
        {
          name: 'user-management',
          displayName: 'User Management',
          meta: {
            icon: 'group',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
        {
          name: 'user-monitoring',
          displayName: 'User Monitoring',
          meta: {
            icon: 'analytics',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
        {
          name: 'faq-management',
          displayName: 'FAQ Management',
          meta: {
            icon: 'help_outline',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
        {
          name: 'admin-billing',
          displayName: 'Billing',
          meta: {
            icon: 'payments',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
        {
          name: 'push-notifications',
          displayName: 'Push Notifications',
          meta: {
            icon: 'notifications_active',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
        {
          name: 'contractor-management',
          displayName: 'Contractor Management',
          meta: {
            icon: 'business',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
        {
          name: 'kpi-management',
          displayName: 'KPI Management',
          meta: {
            icon: 'analytics',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
        },
        {
          name: 'docs',
          displayName: 'Docs',
          meta: {
            icon: 'menu_book',
            allowedRoles: ['admin', 'manager', 'org_admin'],
          },
          children: [
            {
              name: 'docs',
              displayName: 'Main',
              meta: {
                icon: 'menu_book',
                allowedRoles: ['admin', 'manager', 'org_admin'],
              },
            },
            {
              name: 'docs-system',
              displayName: 'System',
              meta: {
                icon: 'hub',
                allowedRoles: ['admin', 'manager', 'org_admin'],
              },
            },
            {
              name: 'docs-api',
              displayName: 'API Docs',
              meta: {
                icon: 'code',
                allowedRoles: ['admin', 'manager', 'org_admin'],
              },
            },
            {
              name: 'docs-playground',
              displayName: 'API Playground',
              meta: {
                icon: 'play_arrow',
                allowedRoles: ['admin', 'manager', 'org_admin'],
              },
            },
          ],
        },
      ],
    },
    {
      name: 'finance',
      displayName: 'Finance',
      meta: {
        icon: 'account_balance',
        allowedRoles: ['admin', 'manager', 'org_admin', 'finance', 'finance-staff'],
      },
      children: [
        {
          name: 'financial-upload',
          displayName: 'Upload Financial Sheet',
          meta: {
            icon: 'upload_file',
            allowedRoles: ['admin', 'manager', 'org_admin', 'finance', 'finance-staff'],
          },
        },
      ],
    },
    {
      name: 'profile',
      displayName: 'Profile',
      meta: {
        icon: 'manage_accounts',
        allowedRoles: [
          'admin',
          'operator',
          'manager',
          'org_admin',
          'owner',
          'analyst',
          'finance',
          'developer',
          'driver',
          'recruiter',
          'support_agent',
          'team_manager',
          'guest',
        ],
      },
    },
    // {
    //   name: 'demo',
    //   displayName: 'Demo',
    //   meta: {
    //     icon: 'science',
    //     allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
    //   },
    //   children: [
    //     {
    //       name: 'demo-uber-region',
    //       displayName: 'Uber Region Demo',
    //       meta: {
    //         icon: 'public',
    //         allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
    //       },
    //     },
    //   ],
    // },
  ],
}

export default sidebarRoutes
